import { z } from "zod";
import { isValidAwardTimeFormat, isValidIanaTimeZone } from "./award-time";
import type { PlayRewards } from "../types";
type RawRewardsDivision = {
  readonly divisionId: string;
  readonly creditsByRank: readonly number[];
};
type RawRewards = {
  readonly enabled: boolean;
  readonly timeZone: string | null;
  readonly awardTime: string | null;
  readonly awardScheduleMisconfigured: boolean;
  readonly divisions: readonly RawRewardsDivision[];
};
function rewardDivision(entry: unknown): RawRewardsDivision | null {
  const parsed = z.record(z.string(), z.unknown()).safeParse(entry);

  if (!parsed.success) return null;

  const division = parsed.data;

  if (typeof division.division_id !== "string") return null;

  const creditsByRank = Array.isArray(division.credits_by_rank)
    ? division.credits_by_rank.filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value),
      )
    : [];
  return { divisionId: division.division_id, creditsByRank };
}

export function parseRewardsSettings(
  settings: Readonly<Record<string, unknown>> | null | undefined,
): RawRewards | null {
  const parsed = z.record(z.string(), z.unknown()).safeParse(settings?.rewards);

  if (!parsed.success) return null;

  const r = parsed.data;
  const divisionsRaw = Array.isArray(r.divisions) ? r.divisions : [];
  const divisions = divisionsRaw
    .map(rewardDivision)
    .filter((division) => division !== null);
  const timeZone = typeof r.time_zone === "string" ? r.time_zone : null;
  const awardTime = typeof r.award_time === "string" ? r.award_time : null;
  return {
    enabled: typeof r.enabled === "boolean" ? r.enabled : false,
    timeZone,
    awardTime,
    awardScheduleMisconfigured:
      timeZone !== null &&
      awardTime !== null &&
      (!isValidIanaTimeZone(timeZone) || !isValidAwardTimeFormat(awardTime)),
    divisions,
  };
}
export function resolveRewardsForDivision({
  raw,
  divisionId,
}: {
  readonly raw: RawRewards | null;
  readonly divisionId: string | null;
}): PlayRewards | null {
  if (!raw) return null;

  const match = divisionId
    ? raw.divisions.find((d) => d.divisionId === divisionId)
    : undefined;
  return {
    enabled: raw.enabled,
    timeZone: raw.timeZone,
    awardTime: raw.awardTime,
    awardScheduleMisconfigured: raw.awardScheduleMisconfigured,
    creditsByRank:
      match && match.creditsByRank.length > 0 ? match.creditsByRank : null,
  };
}
